import { API, Endpoints, Project, Team } from "@brg/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDeepCompareEffect } from "shared/utils/useDeepCompareEffect";
import { generateLongreadPostParams } from "./generateLongreadPostParams";

const EMPTY_LG_COUNT = 0;
const EMPTY_LG_COUNT_PRESENTATION = 1;

export type CountT = {
  name: string;
  count: number;
  id: string;
  params?: Record<string,string>
};

export type LgLengthT = {
  length: number;
  posts: CountT[];
};

export type LengthCountT = {
  ru: LgLengthT;
  en: LgLengthT;
};

export type LongreadCount = {
  ru: any;
  en: any;
}

const DEFAULT_LONGREAD_LENGTH: LengthCountT = {
  ru: { length: 0, posts: [] },
  en: { length: 0, posts: [] },
};

const DEFAULT_LONGREAD = {
  ru: null,
  en: null
}

interface IOptions {
  isReplacement?: boolean;
  trigger?: any;
  full?: boolean;
  requiredPosts?: string[];
}

interface IProps {
  raw: Project | Team;
  opt?: IOptions;
}

export type UseGetLongreadLengthReturn = {
  hasLongread: boolean;
  onSetLongreadCount: (n: number, localLang?: "ru" | "en") => void;
  onGetLongreadCount: () => number;
  includesPosts: CountT[];
  longread: any;
  isCardImport: boolean;
};

export const useGetLongreadLengthSettings = ({
  raw,
  opt = {},
}: IProps): UseGetLongreadLengthReturn => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language as "ru" | "en";

  const hasMounted = useRef(false);
  const triggerRef = useRef<any>(null);
  const [longreads, setLongreads] = useState<LongreadCount>(DEFAULT_LONGREAD);
  const [countObject, setCountObject] = useState<LengthCountT>(DEFAULT_LONGREAD_LENGTH);
  const [postList, setPostList] = useState<CountT[]>([]);

  const vtype = useSelector((state: any) => state.vt.vtype);
  const { isReplacement = false, trigger, requiredPosts = [], full } = opt;

  const getAdditionalInfoVal = (additionalInfo: any[], key: string): string | null => {
    if (!Array.isArray(additionalInfo)) return null;
    return additionalInfo.find((item: any) => item.name === key)?.value ?? null;
  };

  const emptyLgSign = useMemo(() => {
    if (!vtype) return EMPTY_LG_COUNT;
    return ["t-shape", "fixed-ratio-small", "fixed-ratio-large"].includes(vtype as any)
      ? EMPTY_LG_COUNT_PRESENTATION
      : EMPTY_LG_COUNT;
  }, [vtype]);

  const onUpdate = useCallback(() => {
  }, []);

  const onSetLongreadCount = useCallback(
    (n: number, localLang?: "ru" | "en") => {
      if (isReplacement) return;

      const lang = localLang || (raw?.isBilingual ? currentLang : "ru");

      setCountObject((prev) => ({
        ...prev,
        [lang]: { ...prev[lang], length: n },
      }));
      onUpdate();
    },
    [isReplacement, raw?.isBilingual, currentLang, onUpdate]
  );

  const onSetLongread = (data: any, localLang?: "ru" | "en") => {
    const lang = localLang || (raw?.isBilingual ? currentLang : "ru");
      setLongreads((prev) => ({
        ...prev,
        [lang]: data,
      }));
    }

  const onGetLongreadCount = useCallback((): number => {
    if (isReplacement) return emptyLgSign;
    const lang = raw?.isBilingual ? currentLang : "ru";
    return countObject[lang]?.length ?? 0;
  }, [isReplacement, raw?.isBilingual, currentLang, countObject, emptyLgSign]);

  

  const onFetch = useCallback(async () => {
    if (isReplacement || !raw.longreadId) return;

    const ids = raw?.isBilingual && raw.longreadId_en
      ? [raw.longreadId, raw.longreadId_en]
      : [raw.longreadId];

    const allPosts: CountT[] = [];

    await Promise.all(
      ids.map(async (id, index) => {
        try {
          const response = await API.request()
            .endpoint(Endpoints.longread)
            .url("api/post/GetMany")
            .addQuery("idLongread", id)
            .get()
            .send();

          const posts: any[] = await response.json();

          onSetLongreadCount(posts.length, index === 0 ? "ru" : "en");
          if (full) {
            onSetLongread(posts, index === 0 ? "ru" : "en")
          }

          posts.forEach((post) => {
            const style = post.style;
            const postId = post.postId || post.id;

            if (!style) return;

            const existing = allPosts.find((i) => i.name === style);
            if (existing) {
              existing.count += 1;
              existing.id = postId;
              existing.params = generateLongreadPostParams(post)
            } else {
              allPosts.push({ 
                name: style, 
                id: postId, 
                count: 1, 
                params: generateLongreadPostParams(post)});
            }
          });
        } catch (err) {
          console.error(`Проблема в onFetch`, err);
        }
      })
    );

    const finalPostList = requiredPosts.length > 0
      ? requiredPosts.map((style) => {
          const found = allPosts.find((i) => i.name === style);
          return found || { name: style, id: "", count: 0 };
        })
      : allPosts;

    setPostList(finalPostList);
    onUpdate();
  }, [
    raw?.longreadId,
    raw?.longreadId_en,
    raw?.isBilingual,
    isReplacement,
    requiredPosts,
    onSetLongreadCount,
    onUpdate,
  ]);

  useEffect(() => {
    if (!hasMounted.current && raw?.longreadId) {
      hasMounted.current = true;
      onFetch();
    }
  }, [raw?.longreadId, onFetch]);

  useDeepCompareEffect(() => {
    if (hasMounted.current) {
      onFetch();
    }
  }, [countObject]);

  useDeepCompareEffect(() => {
    if (trigger != null && trigger !== triggerRef.current) {
      triggerRef.current = trigger;
      onFetch();
    }
  }, [trigger]);

  const hasLongread = useMemo(() => {
    if (isReplacement) return true;
    if (raw?.isBilingual) {
      return (countObject.ru.length ?? 0) > emptyLgSign && (countObject.en.length ?? 0) > emptyLgSign;
    }
    return (countObject.ru.length ?? 0) > emptyLgSign;
  }, [raw?.isBilingual, countObject, emptyLgSign, isReplacement]);

  const includesPosts = useMemo(() => {
    return postList.filter((item) => item.count > 0)
  }, [postList]);

  const longread = useMemo(() => {
    if (full) {
      return longreads[currentLang]
    }
    return undefined
  } , [longreads, currentLang])

  const isCardImport = useMemo(() => {
    if (postList) {
      const asCoverPost = postList?.find((item: any) => item.name === 'as_cover');
      return asCoverPost?.params?.isCardImport === 'True' || false;
    }
    return false;
  }, [postList]);

  return {
    hasLongread,
    onSetLongreadCount,
    onGetLongreadCount,
    includesPosts,
    longread,
    isCardImport
  };
};import { API, Endpoints, Project, Team } from "@brg/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDeepCompareEffect } from "shared/utils/useDeepCompareEffect";
import { generateLongreadPostParams } from "./generateLongreadPostParams";

const EMPTY_LG_COUNT = 0;
const EMPTY_LG_COUNT_PRESENTATION = 1;

export type CountT = {
  name: string;
  count: number;
  id: string;
  params?: Record<string,string>
};

export type LgLengthT = {
  length: number;
  posts: CountT[];
};

export type LengthCountT = {
  ru: LgLengthT;
  en: LgLengthT;
};

export type LongreadCount = {
  ru: any;
  en: any;
}

const DEFAULT_LONGREAD_LENGTH: LengthCountT = {
  ru: { length: 0, posts: [] },
  en: { length: 0, posts: [] },
};

const DEFAULT_LONGREAD = {
  ru: null,
  en: null
}

interface IOptions {
  isReplacement?: boolean;
  trigger?: any;
  full?: boolean;
  requiredPosts?: string[];
}

interface IProps {
  raw: Project | Team;
  opt?: IOptions;
}

export type UseGetLongreadLengthReturn = {
  hasLongread: boolean;
  onSetLongreadCount: (n: number, localLang?: "ru" | "en") => void;
  onGetLongreadCount: () => number;
  includesPosts: CountT[];
  longread: any;
  isCardImport: boolean;
};

export const useGetLongreadLengthSettings = ({
  raw,
  opt = {},
}: IProps): UseGetLongreadLengthReturn => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language as "ru" | "en";

  const hasMounted = useRef(false);
  const triggerRef = useRef<any>(null);
  const [longreads, setLongreads] = useState<LongreadCount>(DEFAULT_LONGREAD);
  const [countObject, setCountObject] = useState<LengthCountT>(DEFAULT_LONGREAD_LENGTH);
  const [postList, setPostList] = useState<CountT[]>([]);

  const vtype = useSelector((state: any) => state.vt.vtype);
  const { isReplacement = false, trigger, requiredPosts = [], full } = opt;

  const getAdditionalInfoVal = (additionalInfo: any[], key: string): string | null => {
    if (!Array.isArray(additionalInfo)) return null;
    return additionalInfo.find((item: any) => item.name === key)?.value ?? null;
  };

  const emptyLgSign = useMemo(() => {
    if (!vtype) return EMPTY_LG_COUNT;
    return ["t-shape", "fixed-ratio-small", "fixed-ratio-large"].includes(vtype as any)
      ? EMPTY_LG_COUNT_PRESENTATION
      : EMPTY_LG_COUNT;
  }, [vtype]);

  const onUpdate = useCallback(() => {
  }, []);

  const onSetLongreadCount = useCallback(
    (n: number, localLang?: "ru" | "en") => {
      if (isReplacement) return;

      const lang = localLang || (raw?.isBilingual ? currentLang : "ru");

      setCountObject((prev) => ({
        ...prev,
        [lang]: { ...prev[lang], length: n },
      }));
      onUpdate();
    },
    [isReplacement, raw?.isBilingual, currentLang, onUpdate]
  );

  const onSetLongread = (data: any, localLang?: "ru" | "en") => {
    const lang = localLang || (raw?.isBilingual ? currentLang : "ru");
      setLongreads((prev) => ({
        ...prev,
        [lang]: data,
      }));
    }

  const onGetLongreadCount = useCallback((): number => {
    if (isReplacement) return emptyLgSign;
    const lang = raw?.isBilingual ? currentLang : "ru";
    return countObject[lang]?.length ?? 0;
  }, [isReplacement, raw?.isBilingual, currentLang, countObject, emptyLgSign]);

  

  const onFetch = useCallback(async () => {
    if (isReplacement || !raw.longreadId) return;

    const ids = raw?.isBilingual && raw.longreadId_en
      ? [raw.longreadId, raw.longreadId_en]
      : [raw.longreadId];

    const allPosts: CountT[] = [];

    await Promise.all(
      ids.map(async (id, index) => {
        try {
          const response = await API.request()
            .endpoint(Endpoints.longread)
            .url("api/post/GetMany")
            .addQuery("idLongread", id)
            .get()
            .send();

          const posts: any[] = await response.json();

          onSetLongreadCount(posts.length, index === 0 ? "ru" : "en");
          if (full) {
            onSetLongread(posts, index === 0 ? "ru" : "en")
          }

          posts.forEach((post) => {
            const style = post.style;
            const postId = post.postId || post.id;

            if (!style) return;

            const existing = allPosts.find((i) => i.name === style);
            if (existing) {
              existing.count += 1;
              existing.id = postId;
              existing.params = generateLongreadPostParams(post)
            } else {
              allPosts.push({ 
                name: style, 
                id: postId, 
                count: 1, 
                params: generateLongreadPostParams(post)});
            }
          });
        } catch (err) {
          console.error(`Проблема в onFetch`, err);
        }
      })
    );

    const finalPostList = requiredPosts.length > 0
      ? requiredPosts.map((style) => {
          const found = allPosts.find((i) => i.name === style);
          return found || { name: style, id: "", count: 0 };
        })
      : allPosts;

    setPostList(finalPostList);
    onUpdate();
  }, [
    raw?.longreadId,
    raw?.longreadId_en,
    raw?.isBilingual,
    isReplacement,
    requiredPosts,
    onSetLongreadCount,
    onUpdate,
  ]);

  useEffect(() => {
    if (!hasMounted.current && raw?.longreadId) {
      hasMounted.current = true;
      onFetch();
    }
  }, [raw?.longreadId, onFetch]);

  useDeepCompareEffect(() => {
    if (hasMounted.current) {
      onFetch();
    }
  }, [countObject]);

  useDeepCompareEffect(() => {
    if (trigger != null && trigger !== triggerRef.current) {
      triggerRef.current = trigger;
      onFetch();
    }
  }, [trigger]);

  const hasLongread = useMemo(() => {
    if (isReplacement) return true;
    if (raw?.isBilingual) {
      return (countObject.ru.length ?? 0) > emptyLgSign && (countObject.en.length ?? 0) > emptyLgSign;
    }
    return (countObject.ru.length ?? 0) > emptyLgSign;
  }, [raw?.isBilingual, countObject, emptyLgSign, isReplacement]);

  const includesPosts = useMemo(() => {
    return postList.filter((item) => item.count > 0)
  }, [postList]);

  const longread = useMemo(() => {
    if (full) {
      return longreads[currentLang]
    }
    return undefined
  } , [longreads, currentLang])

  const isCardImport = useMemo(() => {
    if (postList) {
      const asCoverPost = postList?.find((item: any) => item.name === 'as_cover');
      return asCoverPost?.params?.isCardImport === 'True' || false;
    }
    return false;
  }, [postList]);

  return {
    hasLongread,
    onSetLongreadCount,
    onGetLongreadCount,
    includesPosts,
    longread,
    isCardImport
  };
};